import React, { useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import RuleIcon from '@mui/icons-material/Rule';
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { JOB_FORM_RULES_ENDPOINT } from '../../../../consts';
import { JobFormRule } from '../../../../types/jobs';
import { JOB_FORM_RULE_FIELDS } from '../../../../components/service/jobFormRules';
import styles from './tenantJobFormRulesList.module.css';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import { TENANT_ROUTES } from '../../tenantConfig.routes';
import { equalsNumberFilterOperators } from '../../../../components/ui/customDataGrid/filterOperators';

export const TenantJobFormRulesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const canView = useHasPermission('service.view_jobformrule');

  useEffect(() => {
    const path = TENANT_ROUTES.jobFormRules;
    setOverride(path, t('Job form rules'));

    return () => {
      clearOverride(path);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: 'services',
      headerName: t('Services'),
      filterable: false,
      sortable: false,
      flex: 1,
      valueGetter: (value: JobFormRule['services']) =>
        (value ?? []).map((service) => service.title).join(', '),
    },
    {
      field: 'required_fields',
      headerName: t('Required fields'),
      filterable: false,
      sortable: false,
      flex: 1,
      valueGetter: (value: JobFormRule['required_fields']) =>
        (value ?? [])
          .map((field) => {
            const labelKey = JOB_FORM_RULE_FIELDS[field.field_type]?.labelKey;
            return labelKey ? t(labelKey) : field.field_type;
          })
          .join(', '),
    },
  ];

  if (!canView) {
    return null;
  }

  return (
    <Box className={styles.container}>
      <CustomDataGrid
        icon={<RuleIcon />}
        title={t('Job form rules')}
        url={JOB_FORM_RULES_ENDPOINT}
        gridSettingsKey="TENANT_JOB_FORM_RULES"
        columns={columns}
        refreshEventName="jobFormRulesUpdated"
        onRowClick={(id: string) => navigate(TENANT_ROUTES.jobFormRule(id))}
      />
    </Box>
  );
};
